/**
 * Limit constants
 *
 * Centralized limit values for connections, caches, validation, and iteration.
 */

// =============================================================================
// Connection Limits
// =============================================================================

/**
 * Maximum WebSocket message size in bytes (32KB)
 */
export const MAX_WS_MESSAGE_BYTES = 32 * 1024;

/**
 * Maximum queued WebSocket messages (frontend)
 */
export const MAX_QUEUED_MESSAGES = 200;

/**
 * Maximum message queue size (game frontend)
 */
export const MAX_MESSAGE_QUEUE_SIZE = 100;

/**
 * WebSocket requests per minute per user
 */
export const WS_REQUESTS_PER_MIN = 60;

/**
 * IP requests per minute
 */
export const IP_REQUESTS_PER_MIN = 120;

/**
 * Default rate limit max requests
 */
export const DEFAULT_MAX_REQUESTS = 100;

// =============================================================================
// Party Limits
// =============================================================================

/**
 * Maximum party size: 1 PC + 3 NPCs, the composition modules are balanced for.
 * Must stay in sync with `MAX_PARTY_SIZE` in `backend/shared/src/constants.rs`.
 */
export const MAX_PARTY_SIZE = 4;

/**
 * Maximum party members to show in visual descriptions
 */
export const MAX_PARTY_VISUAL_MEMBERS = 6;

/**
 * Maximum decisions to keep in memory
 */
export const MAX_DECISIONS_IN_MEMORY = 10_000;

// =============================================================================
// Cache/Memory Limits
// =============================================================================

/**
 * Maximum recent narrative rounds to keep
 */
export const MAX_RECENT_NARRATIVE_ROUNDS = 50;

/**
 * Maximum round history entries
 */
export const MAX_ROUND_HISTORY = 20;

/**
 * Maximum scene snapshots to keep
 */
export const MAX_SCENE_SNAPSHOTS = 20;

/**
 * Maximum cache size for API responses
 */
export const MAX_CACHE_SIZE = 100;

// =============================================================================
// AI Iteration Limits
// =============================================================================

/**
 * Maximum tool iterations for character creation agent
 */
export const MAX_CHAR_CREATION_ITERATIONS = 10;

/**
 * Maximum function call rounds for NPC instances
 */
export const MAX_FUNCTION_CALL_ROUNDS = 10;

/**
 * Maximum conversation history length for NPCs
 */
export const MAX_CONVERSATION_HISTORY_LENGTH = 30;

/**
 * Maximum iterations for engine agent
 */
export const MAX_ENGINE_ITERATIONS = 10;

/**
 * Maximum iterations for roleplay engine
 */
export const MAX_ROLEPLAY_ITERATIONS = 15;

/**
 * Maximum iterations for combat engine
 */
export const MAX_COMBAT_ITERATIONS = 15;

/**
 * Maximum iterations for monster AI controller
 */
export const MAX_MONSTER_AI_ITERATIONS = 15;

// =============================================================================
// Validation Limits
// =============================================================================

/**
 * Maximum character ID length
 */
export const MAX_CHAR_ID_LENGTH = 100;

/**
 * Maximum name field length
 */
export const MAX_NAME_LENGTH = 100;

/**
 * Maximum description field length
 */
export const MAX_DESCRIPTION_LENGTH = 500;

/**
 * Maximum message field length
 */
export const MAX_MESSAGE_LENGTH = 1_000;

/**
 * Search description max display length
 */
export const SEARCH_DESCRIPTION_MAX_DISPLAY = 200;

// =============================================================================
// Background Image Hint Limits
// =============================================================================

/**
 * Minimum overview hint length
 */
export const MIN_OVERVIEW_HINT_LENGTH = 20;

/**
 * Minimum personality hint length
 */
export const MIN_PERSONALITY_HINT_LENGTH = 10;

/**
 * Minimum roleplay hint length
 */
export const MIN_RP_HINT_LENGTH = 20;

// =============================================================================
// Media Generation Limits
// =============================================================================

/**
 * Regenerate media after this many rounds
 */
export const REGENERATE_MEDIA_AFTER_ROUNDS = 10;
